import { useRouter } from 'next/router'
import Script from 'next/script'
import { useEffect } from 'react'
import { useOnMount } from '@captaincause/lib/useOnMount'
import {
  DEFAULT_OPTIONAL_VENDORS,
  OPTIONAL_VENDORS,
  useCookiesConsentStore,
} from './store'

const EXCLUDED_URLS = ['/give', '/demo-', '/preview-', '/impact', '/donation']

function cleanAxeptioVendors(choices: object) {
  return Object.entries(choices).reduce((acc, [vendor, choice]) => {
    if (vendor.startsWith('$$')) return acc

    if (!OPTIONAL_VENDORS.some(optionalVendor => optionalVendor === vendor)) {
      return acc
    }

    const lowerCasedVendor = vendor.toLowerCase()

    return {
      ...acc,
      [lowerCasedVendor]: !!choice,
    }
  }, DEFAULT_OPTIONAL_VENDORS)
}

function axeptioCookieCompleteHandler(
  handler: (choices: object, axeptio: any) => void,
) {
  // Inspired from https://support.axeptio.eu/hc/fr/articles/4404072857745-Int%C3%A9gration-synchronisation-custom
  window._axcb = window._axcb || []

  window._axcb.push((axeptio: any) => {
    axeptio.on('cookies:complete', (choices: object) =>
      handler(choices, axeptio),
    )
  })
}

export function Axeptio() {
  const router = useRouter()

  const { checkIfConsentChanged, init, isInitialized, vendors } =
    useCookiesConsentStore()

  const shouldLoadAxeptio = EXCLUDED_URLS.every(
    url => !router.asPath.startsWith(url),
  )

  useOnMount(() => {
    if (!shouldLoadAxeptio) return

    axeptioCookieCompleteHandler((choices, axeptio) => {
      const axeptioVendors = cleanAxeptioVendors(choices)

      init(axeptioVendors)
      axeptio.off('cookies:complete')
    })
  })

  useEffect(() => {
    if (!isInitialized || !shouldLoadAxeptio) return

    axeptioCookieCompleteHandler(choices => {
      const axeptioVendors = cleanAxeptioVendors(choices)

      const hasConsentChanged = checkIfConsentChanged(axeptioVendors)

      if (hasConsentChanged) {
        router.reload()
      }
    })
  }, [shouldLoadAxeptio, router, isInitialized, checkIfConsentChanged, vendors])

  if (!shouldLoadAxeptio) return null

  return (
    <Script id="axeptio-cookie-banner" strategy="lazyOnload">
      {`window.axeptioSettings = {
          clientId: "${process.env.NEXT_PUBLIC_AXEPTIO_CLIENT_ID}",
          cookiesVersion: "dift-fr-EU",
        };

        (function(d, s) {
          var t = d.getElementsByTagName(s)[0], e = d.createElement(s);
          e.async = true; e.src = "//static.axept.io/sdk.js";
          t.parentNode.insertBefore(e, t);
        })(document, "script");`}
    </Script>
  )
}
